import type { PopoverDismissGuard } from '@frontendlib/components/features/popover/types/config';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Injector, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { ContextMenuWindowComponent } from '@frontendlib/components/features/popover/components/context-menu-window/context-menu-window.component';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { type DragData } from '@modules/drag-n-drop/types/drag-data';
import { QuickActionButtonComponent } from '@modules/elements/components/quick-forms/quick-action-button/quick-action-button.component';
import { PanelQuickFormDrafts } from '@modules/dynamic-panel/services/panel-quick-form-drafts';
import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import { validateNativeDragBatch } from '@sharedlib/common/boards/native-drag-payload';
import { Subject, of } from 'rxjs';
import type { NativeForm } from '../../../services/forms-api.service';
import { BoardsDialogService } from '@modules/boards/services/boards-dialog.service';
import { FormsNativeCreateService, type FormsNativeCreateAttempt } from '../../../services/forms-native-create.service';
import { injectFormStitchyCreate, STITCHY_CREATE_UNAVAILABLE } from '../../shared/form-stitchy-create';
import { FormsCreationPickerComponent } from '../forms-creation-picker.component';

/** Boards' native creation control, adapted only to Forms owner commands and source-copy/link semantics. */
@Component({
  selector: 'app-panel-form-create', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block w-full' },
  templateUrl: './panel-form-create.component.html',
  imports: [QuickActionButtonComponent, ContextMenuItemComponent, ContextMenuWindowComponent, PopoverDirective, DragNDropModule, Button2Component, IconComponent, ReactiveFormsModule],
})
export class PanelFormCreateComponent {
  public readonly folderId = input<string | null>(null);
  public readonly beforeCreate = input<(initiator?: PanelFormCreateComponent) => Promise<boolean>>(async () => true);
  public readonly appearance = input<'rail' | 'panel'>('panel');
  public readonly created = output<NativeForm>();
  private readonly injector = inject(Injector);
  private readonly context = inject(SpaceContextStore);
  private readonly operations = inject(FormsNativeCreateService);
  private readonly changes = inject(FormChangesModalsService);
  private readonly destroy = inject(DestroyRef);
  private readonly guardKey = 'forms-native-create-' + crypto.randomUUID();
  private readonly guardForm = new FormGroup({ pending: new FormControl('') });
  private pending: FormsNativeCreateAttempt | undefined;
  private origin: { tenant: string; generation: number; folder: string | null } | undefined;
  private intent: { ai: boolean; prompt: string } | undefined;
  private preparing = false;
  /** As native `BaseQuickFormComponent` (:176, :214-217, :236): the side panel's app switch asks here before it remounts the body. */
  private readonly panelDrafts = inject(PanelQuickFormDrafts, { optional: true });
  private unregisterPanelDraft?: () => void;
  protected readonly busy = signal(false);
  protected readonly error = signal('');
  protected readonly dragOver = signal(false);
  protected readonly dragBusy = signal(false);
  protected readonly canRetry = signal(false);
  protected readonly canKeep = signal(false);
  protected readonly canCancel = signal(false);
  protected readonly notice = signal('');
  protected readonly aiForm = signal(false);
  protected readonly prompt = new FormControl('', { nonNullable: true });
  protected readonly stitchyCreate = injectFormStitchyCreate();
  protected readonly stitchyUnavailable = STITCHY_CREATE_UNAVAILABLE;
  protected readonly menuChosen = new Subject<void>();
  public readonly mayDismiss: PopoverDismissGuard = { canDismiss: () => this.canLeaveHost(), setOpen: () => undefined };
  public constructor() {
    this.unregisterPanelDraft = this.panelDrafts?.register({
      work: () => this.prompt.value !== '',
      saving: () => this.busy() || this.dragBusy(),
    });
    this.changes.registerForm(this.guardKey, this.guardForm, undefined, undefined, { canLeave: () => this.canLeaveHost() });
    const release = this.context.registerBeforeContextChange?.(() => of(this.canLeaveHost()));
    const beforeUnload = (event: BeforeUnloadEvent): void => { if (!this.canLeaveHost()) event.preventDefault(); };
    window.addEventListener('beforeunload', beforeUnload);
    this.destroy.onDestroy(() => { release?.(); this.unregisterPanelDraft?.(); this.changes.unregisterForm(this.guardKey); window.removeEventListener('beforeunload', beforeUnload); });
  }
  public canLeaveHost(): boolean { return !this.preparing && !this.busy() && !this.dragBusy(); }
  /** Only the initiating host decision may exclude this guard; ordinary departure remains blocked. */
  public get activePreflightGuardKey(): string | undefined { return this.preparing && !this.pending && !this.busy() && !this.dragBusy() ? this.guardKey : undefined; }
  protected readonly accepts = (data: DragData): boolean => !this.busy() && !this.dragBusy() && !this.pending &&
    !!this.context.contextId() && validateNativeDragBatch(data?.data, { purpose: 'create', spaceId: this.context.contextId()! }).ok;
  private current(): boolean {
    const origin = this.origin;
    return !this.destroy.destroyed && !!origin && this.context.contextId() === origin.tenant && this.context.contextChanges() === origin.generation && this.folderId() === origin.folder;
  }
  private async prepare(): Promise<boolean> {
    if (this.preparing || this.busy() || this.dragBusy()) return false;
    if (this.pending) return this.current();
    const tenant = this.context.contextId(), generation = this.context.contextChanges(), folder = this.folderId();
    if (!tenant || this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return false;
    // Keep departure blocked while the host checks every guard except this initiating preflight.
    this.preparing = true;
    try {
      if (!(await this.beforeCreate()(this)) || this.destroy.destroyed || tenant !== this.context.contextId() || generation !== this.context.contextChanges() || folder !== this.folderId()) return false;
      this.origin = { tenant, generation, folder }; return true;
    } finally { this.preparing = false; }
  }
  protected async blank(withStitchy = false): Promise<void> {
    if (withStitchy && (!this.stitchyCreate || !this.prompt.value.trim())) return;
    if (withStitchy && this.prompt.value.trim().length > 4000) return;
    const intent = { ai: withStitchy, prompt: withStitchy ? this.prompt.value.trim() : '' };
    if (!(await this.prepare())) return;
    try { this.pending = this.operations.blank(this.origin!.tenant, this.origin!.folder); this.intent = intent; if (intent.ai) this.pending.assistantPrompt = intent.prompt; await this.settle(false); }
    catch { this.fail(); }
  }
  protected async drop(data: DragData): Promise<void> {
    this.dragOver.set(false);
    if (!this.accepts(data) || !(await this.prepare())) return;
    try { this.pending = this.operations.fromSources(data.data, this.origin!.tenant, this.origin!.folder, 'drag'); this.intent = { ai: false, prompt: '' }; await this.settle(true); }
    catch { this.fail(); }
  }
  protected async retryDrag(): Promise<void> {
    if (this.pending?.finished) { this.pending = undefined; this.intent = undefined; this.canRetry.set(false); this.canKeep.set(false); this.canCancel.set(false); this.error.set(''); this.notice.set('This creation has already finished.'); return; }
    if (this.pending && this.current()) await this.settle(this.pending.units.some(unit => !!unit.source));
  }
  protected async keepDrag(): Promise<void> {
    if (!this.pending || this.busy() || this.dragBusy()) return;
    this.dragBusy.set(true); let kept = false;
    try { await this.operations.keepWithoutLink(this.pending, this.context.contextInjector ?? this.injector, () => this.current()); kept = true; }
    catch (error) { this.error.set(error instanceof Error ? error.message : 'Retry the source Stitch.'); }
    finally { this.dragBusy.set(false); }
    if (kept) await this.retryDrag();
  }
  protected cancelDrag(): void {
    if (!this.pending) return;
    try { const count = this.operations.cancel(this.pending); this.pending = undefined; this.intent = undefined; this.canRetry.set(false); this.canKeep.set(false); this.canCancel.set(false); this.error.set(''); this.notice.set(count ? `${count} created form${count === 1 ? '' : 's'} kept. Remaining items canceled.` : 'Creation canceled.'); }
    catch (error) { this.error.set(error instanceof Error ? error.message : 'Confirm the creation before canceling.'); }
  }
  private fail(): void {
    this.pending = undefined; this.intent = undefined;
    (this.context.contextInjector ?? this.injector).get(ToasterService).show({ text: 'Could not create a form.' });
  }
  private async settle(drag: boolean): Promise<void> {
    const pending = this.pending; if (!pending || this.busy() || this.dragBusy()) return;
    if (drag) this.dragBusy.set(true); else this.busy.set(true);
    this.error.set(''); this.notice.set('');
    try {
      const form = await this.operations.run(pending, this.context.contextInjector ?? this.injector, () => this.current());
      if (!this.current()) return;
      const intent = this.intent;
      this.operations.complete(pending); this.pending = undefined; this.intent = undefined;
      this.busy.set(false); this.dragBusy.set(false); this.canRetry.set(false); this.canKeep.set(false); this.canCancel.set(false);
      this.created.emit(form);
      if (intent?.ai) { this.stitchyCreate?.({ formId: form.id, space: pending.tenant, prompt: intent.prompt }); this.aiForm.set(false); this.prompt.reset(); }
    } catch (error) { if (!this.destroy.destroyed) { if (drag) this.error.set(error instanceof Error ? error.message : 'The form result is unknown. Retry the same request.'); else this.fail(); } }
    finally {
      this.busy.set(false); this.dragBusy.set(false);
      this.canRetry.set(!!this.pending); this.canKeep.set(!!this.pending?.units.some(unit => unit.linkRefused)); this.canCancel.set(!!this.pending && this.operations.canCancel(this.pending));
    }
  }
  protected async choose(kind: 'template' | 'import'): Promise<void> {
    if (!(await this.prepare())) return;
    const origin = this.origin!;
    this.injector.get(BoardsDialogService).showModal({ component: FormsCreationPickerComponent, injector: this.context.contextInjector ?? this.injector,
      title: kind === 'import' ? 'Import a form' : 'From a template',
      content: { mode: kind, folderId: origin.folder, onCreated: (form: NativeForm): boolean => {
        if (this.current()) this.created.emit(form);
        return true; // Picker owns its ACK/close guard; this control never creates or mutates the picked document.
      } },
    });
  }
}
